import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewBrandsLeasing } from "../../Redux/Actions/brandsLeasingActions";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewBrandsLeasing.css";

const ViewBrandsLeasing = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [foundData, setFoundData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(viewBrandsLeasing({ brand_leasing_id: id }))
      .then((res) => {
        if (res && res.statusCode === 200) {
          setFoundData(res.data);
        } else {
          toast.error(res?.message || "Failed to load brands leasing");
        }
      })
      .catch(() => {
        toast.error("Error fetching brands leasing details");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, id]);

  if (loading) {
    return (
      <MainLayout>
        <div className="vcp-loader-wrap">
          <div className="vcp-loader" />
          <p className="vcp-loader-text">Loading brands leasing data…</p>
        </div>
      </MainLayout>
    );
  }

  if (!foundData) {
    return (
      <MainLayout>
        <div className="vcp-empty-wrap">
          <div className="vcp-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 21h18" />
              <path d="M5 21V7l8-4 6 4v14" />
              <path d="M9 9h.01M9 13h.01M9 17h.01M13 9h.01M13 13h.01M13 17h.01" />
            </svg>
          </div>
          <p className="vcp-empty-text">No Brands Leasing Data Found.</p>
          <Link to="/brands_leasing" className="vcp-back-link">← Back to Brands Leasing</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vcp-wrapper">

        {/* ── Header ── */}
        <div className="vcp-header">
          <div className="vcp-header-left">
            <Link to="/brands_leasing" className="vcp-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </Link>
            <div className="vcp-header-divider" />
            <div>
              <div className="vcp-breadcrumb">Brands Leasing / List / View</div>
              <h1 className="vcp-title">{foundData?.name || "No Data"}</h1>
            </div>
          </div>
          <Link to={`/brands_leasing/edit/${id}`} className="vcp-edit-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </Link>
        </div>

        {/* ── Hero Card ── */}
        <div className="vcp-hero-card">
          <div className="vcp-hero-code">
            {foundData?.image && (
              <img
                src={foundData.image}
                alt={foundData.name}
                className="vcp-hero-image"
              />
            )}
            <div>
              <span className="vcp-hero-code-label">Brand Name</span>
              <span className="vcp-hero-code-value">{foundData?.name || "—"}</span>
            </div>
            {foundData?.locable && (
              <span className="vcp-primary-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                </svg>
                Lockable 
              </span>
            )}
          </div>
          <div className="vcp-hero-meta">
            <div className="vcp-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" /><path d="M5 21V7l8-4 6 4v14" />
              </svg>
              {foundData?.project_id?.project_name || "No Project"}
            </div>
            <div className="vcp-hero-meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              {foundData?.type || "No Type"}
            </div>
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div className="vcp-grid-layout">

          {/* Left Column */}
          <div className="vcp-col">

            {/* Basic Info */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">01</span>
                <h2 className="vcp-card-title">Basic Information</h2>
              </div>
              <div className="vcp-info-grid">
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Brand Name</span>
                  <span className="vcp-info-value">{foundData?.name || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Type</span>
                  <span className="vcp-info-value">{foundData?.type || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Project</span>
                  <span className="vcp-info-value">{foundData?.project_id?.project_name || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Lockable</span>
                  <span className={`vcp-inline-status ${foundData?.locable ? "vcp-inline-status--active" : "vcp-inline-status--inactive"}`}>
                    {foundData?.locable ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>

            {/* Lease Details */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">02</span>
                <h2 className="vcp-card-title">Lease Details</h2>
              </div>
              <div className="vcp-info-grid">
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Lease Rent</span>
                  <span className="vcp-info-value">{foundData?.lease_rent || "No Data"}</span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Starting Price</span>
                  <span className="vcp-info-value">
                    {foundData?.starting_price
                      ? `${foundData.currency ? foundData.currency + " " : ""}${foundData.starting_price}`
                      : "No Data"}
                  </span>
                </div>
                <div className="vcp-info-item">
                  <span className="vcp-info-label">Note</span>
                  <span className="vcp-info-value">{foundData?.note || "No Note"}</span>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column */}
          <div className="vcp-col">

            {/* Pricing Card */}
            <div className="vcp-card">
              <div className="vcp-card-header">
                <span className="vcp-card-badge">03</span>
                <h2 className="vcp-card-title">Pricing</h2>
              </div>
              <div className="vcp-discount-single">
                <div className="vcp-discount-pill">
                  <span className="vcp-discount-type">Starting Price</span>
                  <span className="vcp-discount-val">
                    {foundData?.starting_price
                      ? `${foundData.currency ? foundData.currency + " " : ""}${foundData.starting_price}`
                      : "—"}
                  </span>
                </div>
                <div className="vcp-info-item" style={{ marginTop: "12px" }}>
                  <span className="vcp-info-label">Lease Rent</span>
                  <span className="vcp-info-value">{foundData?.lease_rent || "No Data"}</span>
                </div>
              </div>
            </div>

            {/* Image Card */}
            {foundData?.image && (
              <div className="vcp-card">
                <div className="vcp-card-header">
                  <span className="vcp-card-badge">04</span>
                  <h2 className="vcp-card-title">Brand Image</h2>
                </div>
                <div className="vcp-image-wrap">
                  <img
                    src={foundData.image}
                    alt={foundData.name}
                    className="vcp-full-image"
                  />
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ViewBrandsLeasing;